/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Form, Input, Select, Tooltip } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import DataProvider from "../../services";
import _ from "lodash";
import { useAppStore } from "../../store";
import {
  MODAL_TYPES,
  useGlobalModalContext,
} from "../../context/global-modal-context";
import { useAppState } from "../../context/app-state-provider";
import LoadWrapper from "../elements/load-wrapper";
import { SolutionOutlined, WalletOutlined } from "@ant-design/icons";

type NotificationType = "success" | "info" | "warning" | "error";
type PaymentMethod = "mc" | "csh" | "";

export default function AdditionalDetails(props: any) {
  const { showModal } = useGlobalModalContext();
  const { api, contextHolder } = useAppState();
  const { user } = useAppStore();
  const { Option } = Select;
  const [form] = Form.useForm();

  const openNotificationWithIcon = (
    type: NotificationType,
    title: string,
    message: string
  ) => {
    api[type]({
      message: title,
      description: message,
    });
  };

  const [loading, setLoading] = useState(false);
  const [deliveryData, setDeliveryData] = useState<any[]>([]);
  const [insuranceData, setInsuranceData] = useState<any[]>([]);
  const [deliveryMethods, setDeliveryMethods] = useState<any[]>([]);
  const [selectMethod, setMethod] = useState<number | null>(null);
  // Single-value selection is the guardrail itself: a payment method can
  // only ever be "mc", "csh", or unset - never both at once, because
  // there's exactly one field backing both options (see the payment
  // cards below, which are wired to this same value rather than two
  // independent checkboxes).
  const [value, setValue] = useState<PaymentMethod>("");
  const [mcare, setMcare] = useState<any>(null);

  const getAddress = useCallback(async () => {
    try {
      await DataProvider.getDelivery().then(async (res: any) => {
        setDeliveryData(res.data);
      });

      await DataProvider.getInsurance().then(async (res: any) => {
        const data: any = _.sortBy(res.data, "provider");
        setInsuranceData(data);
      });

      if (user && user.phoneNumber) {
        form.setFieldValue("contact", user.phoneNumber);
      }

      await DataProvider.deliveryMethods().then(async (res) => {
        const methods = res.data;
        localStorage.setItem("@type", JSON.stringify(methods));
        setDeliveryMethods(methods);
      });
    } catch (error) {
      openNotificationWithIcon(
        "error",
        "Failed",
        "Couldn't load delivery/insurance details. Please refresh and try again."
      );
    }
  }, []);

  async function fetchAddress() {
    try {
      await DataProvider.getDelivery().then(async (res: any) => {
        setDeliveryData(res.data);
      });
    } catch (error) {
      openNotificationWithIcon(
        "error",
        "Failed",
        "Couldn't load your addresses. Please try again."
      );
    }
  }

  useEffect(() => {
    getAddress();
  }, [getAddress]);

  const getMcare = async () => {
    setLoading(true);
    try {
      const { data } = await DataProvider.getUserSubscription();
      if (data && data.message === "Not a subscriber!") {
        setLoading(false);
        setMcare(null);
        return;
      } else if (data && data.status !== "active") {
        setLoading(false);
        setMcare(null);
        return;
      }
      setMcare(data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setMcare(null);
    }
  };

  useEffect(() => {
    if (value === "mc") {
      getMcare();
    }
  }, [value]);

  function _onProceed() {
    const formX = form.getFieldsValue();
    if (value.length < 1) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Select a payment method!"
      );
    }

    // Guardrail: exactly one payment method must be selected. `value` can
    // only hold a single method by construction, so this should never
    // actually fire - it's a defensive backstop in case that invariant is
    // ever broken by a future change (e.g. re-enabling Cash without
    // wiring it through the same shared value).
    if (value !== "mc" && value !== "csh") {
      return openNotificationWithIcon(
        "error",
        "Payment method",
        "Please select exactly one payment method."
      );
    }

    if (!formX.deliveryMethod) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Delivery method is required!"
      );
    }

    if (selectMethod === 2 && !formX.address) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Kindly select your delivery address!"
      );
    }
    if (!formX.contact) {
      return openNotificationWithIcon(
        "warning",
        "Warning",
        "Phone number is required for delivery!"
      );
    }

    if (value === "mc" && (!mcare || mcare?.status !== "active")) {
      return openNotificationWithIcon(
        "warning",
        "Mcare",
        "You are not subscribed to any package!"
      );
    }

    const insurance = formX.insurance
      ? insuranceData.filter((item) => item.id === formX.insurance)
      : null;

    let address: any;
    if (formX.address) {
      address = deliveryData.find((item) => item.id === formX.address);
    }
    const deliveryMethod = deliveryMethods.filter(
      (item) => item.id === formX.deliveryMethod
    );

    delete formX.address;
    delete formX.deliveryMethod;
    delete formX.insurance;

    props.setPersist({
      ...props.persist,
      ...formX,
      address: address ? address : null,
      deliveryMethod: deliveryMethod[0],
      insurance,
    });
    props.setTab(2);
  }

  const openUserModal = () => {
    showModal(MODAL_TYPES.ADDRESS_MODAL, {
      title: "Add Delivery Address",
      confirmBtn: "Ok",
      fetch: () => fetchAddress(),
    });
  };

  const addressLabel = () => (
    <div className="w-full flex items-center justify-between">
      <span className="">Delivery address</span>
      <Button onClick={openUserModal} type="link">
        Add Address
      </Button>
    </div>
  );

  return (
    <div className="w-full flex justify-center px-4">
      <div className="lg:w-[40%] md:w-[70%] w-full max-w-lg">
        <h2 className="font-semibold text-lg text-neutral-900 text-center">
          Additional information
        </h2>
        <p className="text-sm text-neutral-500 text-center mb-6">
          Tell us how to bill and deliver this order.
        </p>

        <Form form={form} name="basic" layout="vertical" autoComplete="off">
          <div className="mb-5">
            <p className="text-sm font-medium text-neutral-800 mb-2">
              How do you want to pay?
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setValue(value === "mc" ? "" : "mc")}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border text-center transition-colors ${
                  value === "mc"
                    ? "border-[#06509E] bg-[#06509E]/5"
                    : "border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50"
                }`}
              >
                <div
                  className={`h-10 w-10 rounded-full flex items-center justify-center text-lg ${
                    value === "mc"
                      ? "bg-[#06509E] text-white"
                      : "bg-neutral-100 text-neutral-500"
                  }`}
                >
                  <SolutionOutlined />
                </div>
                <span className="font-medium text-sm text-neutral-900">
                  Mcare
                </span>
              </button>

              <Tooltip title="Cash payment is temporarily unavailable. Please pay with Mcare.">
                <button
                  type="button"
                  disabled
                  aria-disabled
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border border-neutral-200 bg-neutral-50 text-center cursor-not-allowed opacity-60"
                >
                  <div className="h-10 w-10 rounded-full bg-neutral-200 text-neutral-400 flex items-center justify-center text-lg">
                    <WalletOutlined />
                  </div>
                  <span className="font-medium text-sm text-neutral-500">
                    Cash
                  </span>
                  <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wide -mt-1">
                    Coming soon
                  </span>
                </button>
              </Tooltip>
            </div>
          </div>

          {value === "mc" && (
            <div className="mb-5">
              {loading ? (
                <LoadWrapper
                  size="small"
                  tip="checking mcare package..."
                  loading={loading}
                >
                  <div className="border border-neutral-200 bg-[#F0F9FF] rounded-xl md:h-16 h-12" />
                </LoadWrapper>
              ) : !loading && !mcare ? (
                <p className="text-red-600 text-sm bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                  You are not subscribed to any package.
                </p>
              ) : !loading && mcare ? (
                <div className="border border-[#06509E]/20 bg-[#06509E]/5 rounded-xl p-3">
                  <h4 className="text-neutral-800 font-medium text-sm">
                    {mcare.mcare.description}
                  </h4>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {mcare.mcare.offers.map((rea: any, idx: any) => (
                      <span
                        key={idx}
                        className="text-xs text-neutral-500 mr-1"
                      >
                        {rea.name}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}

          <Form.Item name="deliveryMethod" label="Delivery method">
            <Select
              size="large"
              showSearch
              placeholder="Select delivery method"
              onChange={(itx) => setMethod(itx)}
            >
              {deliveryMethods.map((v, index) => (
                <Option key={index} value={v.id}>
                  <p>{v.description}</p>
                </Option>
              ))}
            </Select>
          </Form.Item>
          {selectMethod === 2 && (
            <div>
              {addressLabel()}
              <Form.Item name="address" label="">
                <Select
                  size="large"
                  showSearch
                  placeholder="Select delivery address"
                >
                  {deliveryData.map((v, index) => (
                    <Option key={index} value={v.id}>
                      <p>{v.town}</p>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          )}

          <Form.Item name="contact" label="Phone Number">
            <Input
              size="large"
              type="number"
              placeholder="Enter phone number"
            />
          </Form.Item>

          <Form.Item name="notes" label="Additional information">
            <Input
              size="large"
              placeholder="Additional information allergies, etc."
            />
          </Form.Item>

          <Form.Item className="mb-3">
            <Button
              onClick={_onProceed}
              type="primary"
              size="large"
              htmlType="submit"
              style={{ backgroundColor: "#06509E" }}
              className="w-full"
            >
              Continue
            </Button>
          </Form.Item>

          <Button
            size="large"
            onClick={() => props.setTab(0)}
            className="w-full"
          >
            Back
          </Button>
        </Form>
      </div>
      {contextHolder}
    </div>
  );
}
